import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, timeout} from 'rxjs/operators';
import {ProductResource} from '@services/resource/product-resource';
import {Observable} from 'rxjs';
import {ConfigService} from '@services/infra/config.service';
import {ErrorService} from '@services/error.service';
import {ClientConfig} from '@app/client-config';
import {UUID} from '@util/types';
import {Option} from 'fp-ts/lib/Option';
import {SampleResource} from '@services/resource/sample-resource';
import {optional} from '@util/http';
import {Product} from '@services/model/product';
import {Produktblatt, ProduktblattResource} from '@services/model/produktblatt';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  constructor(
    private readonly errors: ErrorService,
    private readonly http: HttpClient,
    private readonly conf: ConfigService,
  ) {}

  public saveProduct(productResource: ProductResource): Observable<Product> {
    const params = new HttpParams();

    const url = `${this.conf.backendUrl}/v1/product/${productResource.id}`;
    return this.http
      .put<ProductResource>(url, productResource, {params, withCredentials: true})
      .pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler(), map(Product.fromResource));
  }

  public getProduct(productId: string): Observable<Product> {
    const url = `${this.conf.backendUrl}/v1/product/${productId}`;
    return this.http.get<ProductResource>(url).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler(), map(Product.fromResource));
  }

  public getProduktblatt(productId: string): Observable<Produktblatt> {
    const url = `${this.conf.backendUrl}/v1/product/${productId}/sheet`;
    return this.http.get<ProduktblattResource>(url).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler(), map(Produktblatt.fromResource));
  }

  public getIsEditable(productId: string): Observable<boolean> {
    const url = `${this.conf.backendUrl}/v1/product/${productId}/editable`;
    return this.http.get<boolean>(url).pipe(timeout(ClientConfig.timeout), this.errors.withErrorHandler());
  }

  public productForUser(userId: string): Observable<Product> {
    const url = `${this.conf.backendUrl}/v1/user/${userId}/product`;
    return this.http.get<ProductResource>(url).pipe(
      timeout(ClientConfig.timeout),
      this.errors.withErrorHandler(),
      map((sr) => Product.fromResource(sr)),
    );
  }

  public requestSample(productId: UUID, companyId: UUID, text: string): Observable<Option<SampleResource>> {
    const url = `${this.conf.backendUrl}/v1/product/${productId}/sample-request/${companyId}`;
    return this.http.put<SampleResource>(url, {text}).pipe(optional());
  }

  public getSampleRequest(productId: UUID, companyId: UUID): Observable<Option<SampleResource>> {
    const url = `${this.conf.backendUrl}/v1/product/${productId}/sample-request/${companyId}`;
    return this.http.get<SampleResource>(url).pipe(optional());
  }
}
